import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link } from '@resy/ui-kitchen';
import { sanitizeString } from '../../../../../../../ui-kitchen/src/utils/sanitizeString';
import { getRetinaImage, handleMktAnalytics, scrollTo } from '../../../AmexMarketingUtils/AmexUtils';
import { CTA_EVENT_NAME } from '../../../AmexMarketingUtils/constants';
import './AmexPlatinumNightsBenefitsCard.scss';

let hashTimeout = null;

const AmexPlatinumNightsBenefitsCard = (
  {
    Analytics,
    awsFolderName,
    baseRequestUrl,
    matchMedia,
    cardConfig: {
      badge,
      title,
      sub_title: subTitle,
      copy,
      image: {
        desktop: desktopImg,
        mobile: mobileImg,
      },
      cta,
    },
    utm,
    index,
    componentCallback,
  }
) => {
  const urlPrefix = `${baseRequestUrl}${awsFolderName}/images/`;
  const imgName = getRetinaImage(desktopImg, mobileImg, matchMedia.lg);
  const image = `${urlPrefix}${imgName}`;

  useEffect(() => {
    if (hashTimeout) clearTimeout(hashTimeout);
  }, []);

  const sanitizedBadge = sanitizeString(badge);
  const sanitizedTitle = sanitizeString(title);
  const sanitizedSubtitle = sanitizeString(subTitle);
  const sanitizedCopy = sanitizeString(copy);

  const handleClick = () => {
    const extraParams = {
      action_name: 'Benefits - Learn More Click',
      item_name: sanitizedTitle,
    };
    handleMktAnalytics(Analytics?.event, CTA_EVENT_NAME, extraParams);
  };

  const displayCTA = () => {
    const { id, label, url } = cta;
    const ctaUrl = utm ? `${url}${utm}` : url;

    switch (id) {
      case 'platinum':
        return (
          <Link
            href={window?.location?.href?.split('#')[0] + url}
            className="AmexPlatinumNightsBenefitsCard__cta platinum"
            aria-describedby={`benefitTitle${index}`}
            onContextMenu={handleClick}
            onClick={() => {
              handleClick();
              const currentHash = url.replace('#', '');
              componentCallback('hash', currentHash);
              hashTimeout = setTimeout(() => scrollTo(url), 1000);
            }}
          >
            {label}
          </Link>
        );
      default:
        return (
          <Link
            className="AmexPlatinumNightsBenefitsCard__cta"
            target="_blank"
            href={ctaUrl}
            aria-describedby={`benefitTitle${index}`}
            onClick={() => {
              handleClick();
            }}
          >
            {label}
          </Link>
        );
    }
  };

  return (
    <div
      className="AmexPlatinumNightsBenefitsCard"
      role="group"
      aria-labelledby={`benefitTitle${index}`}
    >
      <div className="AmexPlatinumNightsBenefitsCard__content">
        {title && (
          <h3
            id={`benefitTitle${index}`}
            className="AmexPlatinumNightsBenefitsCard__title"
            dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
              __html: sanitizedTitle,
            }}
          />
        )}
        {badge && (
          <p
            className="AmexPlatinumNightsBenefitsCard__badge"
            dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
              __html: sanitizedBadge,
            }}
          />
        )}
        {subTitle && (
          <p
            className="AmexPlatinumNightsBenefitsCard__subtitle"
            dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
              __html: sanitizedSubtitle,
            }}
          />
        )}
        {copy && (
          <p
            className="AmexPlatinumNightsBenefitsCard__copy"
            dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
              __html: sanitizedCopy,
            }}
          />
        )}
        {displayCTA()}
      </div>
      {imgName && (
        <img className="AmexPlatinumNightsBenefitsCard__img" src={image} alt="" />
      )}
    </div>
  );
};

export default AmexPlatinumNightsBenefitsCard;

AmexPlatinumNightsBenefitsCard.propTypes = {
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  awsFolderName: PropTypes.string.isRequired,
  baseRequestUrl: PropTypes.string.isRequired,
  cardConfig: PropTypes.shape({
    image: PropTypes.shape({
      desktop: PropTypes.shape({}).isRequired,
      mobile: PropTypes.shape({}).isRequired,
    }).isRequired,
    badge: PropTypes.string,
    title: PropTypes.string.isRequired,
    sub_title: PropTypes.string,
    copy: PropTypes.string.isRequired,
    card_image: PropTypes.shape({
      desktop: PropTypes.shape({}).isRequired,
      mobile: PropTypes.shape({}).isRequired,
    }),
    cta: PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  utm: PropTypes.string,
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
  index: PropTypes.number.isRequired,
  componentCallback: PropTypes.func,
};

AmexPlatinumNightsBenefitsCard.defaultProps = {
  utm: null,
  componentCallback: () => {},
};
